/**
 * MKS-364 — the "Time booking" cell of the ticket slide-over's attribute grid:
 * whether the ticket is closed for time booking, and the manager's toggle.
 *
 * Its own component rather than inline JSX in `TaskDetailSlideOver`, for the
 * reason `BillingPeriodField` and `TaskTierPicker` next to it are: it owns a
 * hook. Inlining it would mount `useSetTaskTimeBookingLock` — and with it a
 * `useQueryClient` — on every render of the slide-over, including the
 * MyTasks / SLA-dashboard surfaces that never show the lock. Here the hook is
 * mounted only where the cell is actually rendered.
 *
 * `canManage` gates the BUTTON, never the state: a person about to book time
 * should learn the ticket is closed from this row rather than from a refused
 * time entry. The gate is not a security boundary either way — the backend
 * re-checks admin-OR-project-PM on every request.
 */
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useSetTaskTimeBookingLock } from '../../hooks/useSetTaskTimeBookingLock';

export interface TaskTimeBookingLockFieldProps {
  taskId: number;
  projectId: number;
  /**
   * `task.timeBookingLocked` verbatim. Optional on the wire type so pre-MKS-364
   * fixtures compile, which is why every read below is `=== true`: an absent
   * key means "open", not "unknown".
   */
  locked: boolean | undefined;
  /** Admin or the project's PM — see the file header on what this does NOT do. */
  canManage: boolean;
  onError?: (message: string) => void;
}

export function TaskTimeBookingLockField({
  taskId,
  projectId,
  locked,
  canManage,
  onError,
}: TaskTimeBookingLockFieldProps) {
  const mutation = useSetTaskTimeBookingLock();
  const isLocked = locked === true;

  // Send the NEGATION of what is on screen, so the control is a true toggle and
  // one gate covers both directions (MKS-364: "same permission gate both ways").
  function handleToggle() {
    mutation.mutate(
      { projectId: String(projectId), taskId, locked: !isLocked },
      {
        onError: (err) => {
          if (onError) onError(err.message ?? 'Server error');
        },
      },
    );
  }

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-ms-text-secondary">Time booking</dt>
      <dd className="flex items-center gap-2">
        {isLocked ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Locked
          </span>
        ) : (
          <span className="font-medium text-ms-text">Open</span>
        )}
        {canManage && (
          <button
            type="button"
            onClick={handleToggle}
            disabled={mutation.isPending}
            className="text-xs font-medium text-primary-600 hover:underline disabled:opacity-50"
          >
            {isLocked ? 'Unlock' : 'Lock'}
          </button>
        )}
      </dd>
    </div>
  );
}
